import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPercentage } from '@/utils/formatters.ts'
import { RankRow } from './ranking-card.types.ts'
import { RankCardColors } from './ranking-card.tsx'
import { RankingHighlight, HighlightItem } from './ranking-highlight.tsx'
import { useRankCards, usdText } from './use-rank-cards.ts'

// See overall-ranking-card.tsx: CSS variables keep this object constant across
// a theme toggle.
const HIGHLIGHT_COLORS: RankCardColors = {
  cardBg: 'var(--sum-card-bg)',
  cardOutline: 'var(--color-primary-border-gold)',
  listCardOutline: 'var(--color-border)',
  linkColor: 'var(--book-name-link)',
  tertiaryTextColor: 'var(--color-tertiary-text)',
  tagBg: 'var(--card-more-bg)',
  longBg: 'var(--color-long-bg)',
  longText: 'var(--ag-grid-table-green)',
  shortBg: 'var(--color-short-bg)',
  shortText: 'var(--ag-grid-table-red)',
}

interface OverallRankingHighlightProps {
  rankingList: RankRow[]
  groupByFirm: boolean
  startDate: string | null
  endDate: string | null
}

// Desktop container for the top-3 highlight strip. Resolves the same card view
// models the mobile view uses and maps theme tokens to card colours.
export const OverallRankingHighlight: React.FC<
  OverallRankingHighlightProps
> = ({ rankingList, groupByFirm, startDate, endDate }) => {
  const { t } = useTranslation()

  const { hasSingleFirst, toCardData } = useRankCards({
    rankingList,
    groupByFirm,
    startDate,
    endDate,
  })

  const items: HighlightItem[] = useMemo(
    () =>
      hasSingleFirst
        ? rankingList
            .filter((row) => row.rank != null && row.rank <= 3)
            .map((row) => {
              const data = toCardData(row)
              return {
                data,
                returnPctText: formatPercentage(data.returnPct),
                cumulativePnlText: usdText(data.cumulativePnl),
                dailyPnlText: usdText(data.dailyPnl),
              }
            })
        : [],
    [rankingList, toCardData, hasSingleFirst]
  )

  if (items.length === 0) return null

  return (
    <RankingHighlight
      items={items}
      colors={HIGHLIGHT_COLORS}
      dailyLabel={t('overall_ranking.mobile_card.daily')}
    />
  )
}
